import { spriteUrl, itemSpriteUrl } from "@/lib/api";

const TRAINER_SPRITE_URL = (spriteId) =>
  `https://play.pokemonshowdown.com/sprites/trainers/${spriteId || "youngster"}.png`;

const TYPE_COLORS = {
  discovery: "#4A4A4A",
  capture: "#2E4F32",
  miss: "#7A7A7A",
  train: "#4A4A4A",
  badge: "#B87333",
  defeat: "#8C2A2A",
  league_win: "#B87333",
  league_loss: "#8C2A2A",
  level_up: "#2E4F32",
  pokemon_level_up: "#2E4F32",
  evolution: "#B87333",
  item_found: "#B87333",
  no_pokeball: "#8C2A2A",
  stamina_low: "#8C2A2A",
  train_fail: "#8C2A2A",
  charm_used: "#B87333",
  item_used: "#2E4F32",
  attack_boost: "#B87333",
  release: "#7A7A7A",
  recruit: "#1A1A1A",
  pc_train: "#4A4A4A",
  charm_available: "#B87333",
  charm_assigned: "#B87333",
  region_unlock: "#2E4F32",
  region_move: "#4A4A4A",
  legendary_catch: "#B87333",
  legendary_miss: "#7A7A7A",
  encounter_win: "#B87333",
  info: "#7A7A7A",
  phone: "#4A4A4A",
  grade_up: "#B87333",
};

function timeFmt(iso) {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}`;
}

// Special illustration types for events without a natural sprite fallback.
const EVENT_ILLUSTRATION = {
  region_unlock: "/img/lore/region_unlock_banner.png",
};

// Determine the best small illustration URL for a given event.
// The trainer sprite is used as a "portrait" alongside pokemon/item icons —
// for events tied to a trainer's action, the trainer face makes the journal
// feel much more alive than a colored dot.
function eventThumbUrl(e, trainerSpriteById) {
  const meta = e.meta || {};
  // 1) Pokemon-related events → pokemon sprite
  if (meta.pokemon_id) {
    if (meta.is_shiny) {
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${meta.pokemon_id}.png`;
    }
    // Discovery events show a black silhouette via CSS filter below
    return spriteUrl(meta.pokemon_id);
  }
  // 2) Item events → item sprite
  if (meta.item_id) return itemSpriteUrl(meta.item_id);
  // 3) Special illustration types
  if (EVENT_ILLUSTRATION[e.type]) return EVENT_ILLUSTRATION[e.type];
  // 4) Trainer-anchored fallback → their trainer sprite (recruit, badges,
  //    defeats, phone directives, region moves, etc. all attach a trainer_id).
  const spriteId = e.trainer_sprite_id
    || (e.trainer_id && trainerSpriteById && trainerSpriteById[e.trainer_id]);
  if (spriteId) return TRAINER_SPRITE_URL(spriteId);
  return null;
}

function EventThumb({ event, trainerSpriteById }) {
  const url = eventThumbUrl(event, trainerSpriteById);
  const isDiscovery = event.type === "discovery" && event.meta?.pokemon_id;
  const isTrainerPortrait = !!(url && url.includes("play.pokemonshowdown.com/sprites/trainers/"));
  if (!url) {
    // Minimal fallback: colored dot in the type's color
    return (
      <div
        className="w-9 h-9 shrink-0 border border-[#2B2B2B] flex items-center justify-center bg-[#EAE4D9]"
        style={{ color: TYPE_COLORS[event.type] || "#4A4A4A" }}
      >
        <span className="font-serif-display text-lg">•</span>
      </div>
    );
  }
  const isBanner = url.startsWith("/img/lore/");
  return (
    <div className="w-9 h-9 shrink-0 border border-[#2B2B2B] bg-[#FCF9F2] flex items-center justify-center overflow-hidden">
      <img
        src={url}
        alt=""
        className={`pixelated max-w-full max-h-full ${isBanner ? "object-cover w-full h-full" : ""} ${isTrainerPortrait ? "object-contain" : ""}`}
        style={{ filter: isDiscovery ? "grayscale(100%) brightness(0%)" : "none" }}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    </div>
  );
}

export default function ActivityFeed({ events, dense = false, trainers = [] }) {
  // Build a trainer_id → sprite_id lookup so events without an embedded
  // trainer_sprite_id (legacy rows from before this feature) still show the
  // right portrait based on the currently-known trainer list.
  const trainerSpriteById = {};
  for (const t of trainers || []) {
    if (t && t.id && t.sprite_id) trainerSpriteById[t.id] = t.sprite_id;
  }
  if (!events || events.length === 0) {
    return (
      <div className="text-xs text-[#7A7A7A] font-mono p-4 text-center" data-testid="activity-empty">
        — Aucune activité pour le moment. Recrute un dresseur pour démarrer l&apos;aventure —
      </div>
    );
  }
  return (
    <ul className="ruled" data-testid="activity-feed">
      {events.map((e, i) => (
        <li
          key={e.id || i}
          className={`flex items-start gap-2 px-2 py-1.5 row-hover ${i % 2 ? "bg-[#FCF9F2]" : "bg-transparent"}`}
          data-testid={`activity-row-${i}`}
        >
          {!dense && <EventThumb event={e} trainerSpriteById={trainerSpriteById}/>}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-[#7A7A7A] shrink-0">
                [{timeFmt(e.ts)}]
              </span>
              {!dense && (
                <span
                  className="font-mono text-[9px] uppercase tracking-widest px-1 border shrink-0"
                  style={{ color: TYPE_COLORS[e.type] || "#4A4A4A", borderColor: TYPE_COLORS[e.type] || "#4A4A4A" }}
                >
                  {e.type}
                </span>
              )}
            </div>
            <span className="text-[13px] leading-tight block mt-0.5" style={{ color: TYPE_COLORS[e.type] || "#1A1A1A" }}>
              {e.message}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}
